import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ChevronLeft, SkipForward, Check, X } from 'lucide-react';

const CARD_W = 360;
const CARD_H = 500;
const GAP    = 14;
const EDGE   = 12;

function computePosition(element, isMobile) {
  if (isMobile) return { bottom: 12, left: 0, right: 0, top: 'auto', arrowSide: null };
  if (!element) return { bottom: 32, left: 32, top: 'auto', right: 'auto', arrowSide: null };
  const r  = element.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampLeft = x => Math.max(EDGE, Math.min(x, vw - CARD_W - EDGE));
  const clampTop  = y => Math.max(EDGE, Math.min(y, vh - CARD_H - EDGE));
  if (vh - r.bottom >= CARD_H + GAP) return { top: r.bottom + GAP,      left: clampLeft(r.left + r.width / 2 - CARD_W / 2), bottom: 'auto', right: 'auto', arrowSide: 'top'    };
  if (r.top         >= CARD_H + GAP) return { bottom: vh - r.top + GAP, left: clampLeft(r.left + r.width / 2 - CARD_W / 2), top: 'auto',    right: 'auto', arrowSide: 'bottom' };
  if (vw - r.right  >= CARD_W + GAP) return { left: r.right + GAP,      top: clampTop(r.top + r.height / 2 - CARD_H / 2),   bottom: 'auto', right: 'auto', arrowSide: 'left'   };
  if (r.left        >= CARD_W + GAP) return { right: vw - r.left + GAP, top: clampTop(r.top + r.height / 2 - CARD_H / 2),   bottom: 'auto', left: 'auto',  arrowSide: 'right'  };
  return { bottom: 32, left: 32, top: 'auto', right: 'auto', arrowSide: null };
}

function Arrow({ side, accent }) {
  if (!side) return null;
  const s = 11;
  const base = { position: 'absolute', width: 0, height: 0 };
  const shapes = {
    top:    { ...base, top: -s,    left: '50%', transform: 'translateX(-50%)', borderLeft: `${s}px solid transparent`, borderRight: `${s}px solid transparent`, borderBottom: `${s}px solid #0c1118` },
    bottom: { ...base, bottom: -s, left: '50%', transform: 'translateX(-50%)', borderLeft: `${s}px solid transparent`, borderRight: `${s}px solid transparent`, borderTop:    `${s}px solid #0c1118` },
    left:   { ...base, left: -s,   top: '50%',  transform: 'translateY(-50%)', borderTop:  `${s}px solid transparent`, borderBottom: `${s}px solid transparent`, borderRight:  `${s}px solid #0c1118` },
    right:  { ...base, right: -s,  top: '50%',  transform: 'translateY(-50%)', borderTop:  `${s}px solid transparent`, borderBottom: `${s}px solid transparent`, borderLeft:   `${s}px solid #0c1118` },
  };
  return <div style={shapes[side]} />;
}

function waitForElement(selectors, onFound, timeoutMs) {
  var ms = timeoutMs || 5000;
  if (!selectors || selectors.length === 0) { onFound(null); return function() {}; }
  var done = false, tid = null, poll = null, obs = null;
  function resolve(el) { if (done) return; done = true; clearTimeout(tid); clearInterval(poll); if (obs) obs.disconnect(); onFound(el); }
  function query() { for (var i = 0; i < selectors.length; i++) { try { var el = document.querySelector(selectors[i]); if (el) return el; } catch (e) {} } return null; }
  var found = query(); if (found) { resolve(found); return function() {}; }
  obs = new MutationObserver(function() { var el = query(); if (el) resolve(el); });
  obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'id'] });
  poll = setInterval(function() { var el = query(); if (el) resolve(el); }, 150);
  tid  = setTimeout(function() { resolve(null); }, ms);
  return function() { done = true; clearTimeout(tid); clearInterval(poll); if (obs) obs.disconnect(); };
}

// Was trimmed to a single-page (/feed) tour after it used to hop the user
// across /search, /programs, /messages, a notifications modal, an
// organization page, /settings and /community — too many pages meant that as
// soon as someone navigated away from wherever the tour currently expected
// them to be, it was left with nothing to highlight and just sat there,
// unchanged, on top of whatever page they were actually on.
//
// Greencoin Market and Buddy were added back deliberately, not as a full
// reversion — each new step's `path` is a page the tour itself navigates to
// (via `navigate(s.path)` below), and its `selectors` target that page's
// always-present root container (`.shp-store`, `.bd-hero`), not something
// that only exists after a user interaction (a modal, a loaded list, etc.).
// That's what made the original multi-page tour fragile — a step waiting on
// something conditional — not multi-page navigation itself, which this file
// already drives correctly via the `location.pathname` effect below.
const STEPS = [
  { id: 'welcome',       title: 'Welcome to Greenwich!',      subtitle: 'Your new home for connection',      content: "We're so happy to see you here! Hi, I'm Erika AI, and I'll guide you as you explore and discover everything our platform has to offer.",                                                                                      character: '/nkarner/mascot-welcome.png',     expression: 'Happy to meet you! ',    selectors: [],                                                                                                                                                            path: '/feed',                 accent: '#4ade80' },
  { id: 'navigation',    title: 'Navigation Menu',             subtitle: 'Your compass around Greenwich',     content: "This is your navigation menu! From here you can jump to Home, Profile, Messages, and Notifications.",                                                                                                                         character: '/nkarner/mascot-navigation.png', expression: 'Navigate with Greenwich!', selectors: ['nav', 'header', '.navbar'],                                                                                                                              path: '/feed',                 accent: '#60a5fa' },
  { id: 'create-post',   title: 'Create a Post',               subtitle: 'Share your voice',                  content: "Write your thoughts, upload photos, share videos, and tell the world your story. Your voice matters, let everyone hear it!",                                                                                                    character: '/nkarner/mascot-create-post.png', expression: 'Create something! ',      selectors: ['.create-post'],                                                                                                                                          path: '/feed',                 accent: '#fb7185' },
  { id: 'feed-posts',    title: 'Your Feed',                   subtitle: 'All the action in one place',       content: "Here you'll see posts from people and organizations you follow. Like, comment, save, and share!",                                                                                                                            character: '/nkarner/mascot-feed.png', expression: 'Feed of amazing content!', selectors: ['.feed-posts'],                                                                                                                                           path: '/feed',                 accent: '#f472b6' },
  { id: 'greencoins',    title: 'Greencoin Market',            subtitle: 'Spend what you earn',               content: "Earn Greencoins by being active, then spend them here on real rewards and perks in the marketplace.",                                                                                                                         character: '/nkarner/ai-mascot.png',   expression: 'Shop time! ',              selectors: ['.shp-store', '.shp-page'],                                                                                                                                               path: '/shop',                 accent: '#facc15' },
  { id: 'buddy',         title: 'Greenwich Buddy',             subtitle: 'Find a mentor, or become one',      content: "Buddy connects you with a mentor who's been where you want to go, or lets you become one yourself and guide someone else.",                                                                                                      character: '/nkarner/buddy-mascot.png', expression: 'Let\'s connect! ',         selectors: ['.bd-hero', '.bd-page'],                                                                                                                                                  path: '/buddy',                accent: '#a78bfa' },
  { id: 'final',         title: "You're All Set!",             subtitle: 'Welcome to the Greenwich!',         content: "You now know all the main features of Greenwich. Be kind, share amazing content, connect with wonderful people, and have fun!",                                                                                              character: '/nkarner/mascot-final.png', expression: 'Welcome! ',                selectors: [],                                                                                                                                                            path: '/feed',    final: true,          accent: '#4ade80' },
];

const OnboardingGuide = ({ onComplete, onSkip }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible,   setIsVisible]   = useState(true);
  const [animating,   setAnimating]   = useState(false);
  const [cardPos,     setCardPos]     = useState({ bottom: 32, left: 32, top: 'auto', right: 'auto', arrowSide: null });
  const [isMobile,    setIsMobile]    = useState(typeof window !== 'undefined' ? window.innerWidth <= 600 : false);

  const navigate = useNavigate();
  const location = useLocation();

  const cancelRef     = useRef(null);
  const activeElRef   = useRef(null);
  const navigatingRef = useRef(false);
  const notifOpenRef  = useRef(false);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    STEPS.forEach(s => { const img = new Image(); img.src = s.character; });
  }, []);

  function doOpenNotif()  { if (notifOpenRef.current) return; notifOpenRef.current = true;  window.dispatchEvent(new CustomEvent('onboarding:open-notif-modal')); }
  function doCloseNotif() { if (!notifOpenRef.current) return; notifOpenRef.current = false; window.dispatchEvent(new CustomEvent('onboarding:close-notif-modal')); }
  function clearHL() { document.querySelectorAll('.onboarding-highlight').forEach(el => el.classList.remove('onboarding-highlight')); activeElRef.current = null; }
  function applyHL(el) {
    clearHL();
    if (el) {
      el.classList.add('onboarding-highlight');
      activeElRef.current = el;
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(() => setCardPos(computePosition(el, isMobile)), 420);
    } else {
      setCardPos(computePosition(null, isMobile));
    }
  }

  useEffect(() => {
    const s = STEPS[currentStep];
    if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }
    clearHL();
    if (s.openNotifModal) {
      doOpenNotif();
      const id = setTimeout(() => { cancelRef.current = waitForElement(['.notif-panel'], applyHL, 4000); }, 150);
      return () => { clearTimeout(id); if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; } };
    }
    doCloseNotif();
    if (s.path && location.pathname !== s.path) { navigatingRef.current = true; navigate(s.path); }
    else { navigatingRef.current = false; cancelRef.current = waitForElement(s.selectors, applyHL, 5000); }
    return () => { if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; } };
  }, [currentStep]); // eslint-disable-line

  useEffect(() => {
    if (!navigatingRef.current) return;
    navigatingRef.current = false;
    if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }
    clearHL();
    cancelRef.current = waitForElement(STEPS[currentStep].selectors, applyHL, 5000);
    return () => { if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; } };
  }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    const fn = () => { if (activeElRef.current) setCardPos(computePosition(activeElRef.current, isMobile)); };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [isMobile]);

  useEffect(() => () => { if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; } clearHL(); doCloseNotif(); }, []); // eslint-disable-line

  function goToStep(i) {
    if (animating) return;
    doCloseNotif(); setAnimating(true); clearHL();
    if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }
    setTimeout(() => { setCurrentStep(i); setAnimating(false); }, 200);
  }
  function handleNext()     { if (currentStep < STEPS.length - 1) goToStep(currentStep + 1); else handleComplete(); }
  function handlePrev()     { if (currentStep > 0) goToStep(currentStep - 1); }
  function handleSkip()     { doCloseNotif(); clearHL(); if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; } setIsVisible(false); if (onSkip) onSkip(); }
  function handleComplete() { doCloseNotif(); clearHL(); if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; } setIsVisible(false); if (onComplete) onComplete(); }

  if (!isVisible) return null;

  const s      = STEPS[currentStep];
  const accent = s.accent || '#4ade80';
  const glow   = accent + '55';
  const stepTitle       = t(`onboarding.steps.${s.id}.title`);
  const stepSubtitle    = t(`onboarding.steps.${s.id}.subtitle`);
  const stepContent     = t(`onboarding.steps.${s.id}.content`);
  const stepExpression  = t(`onboarding.steps.${s.id}.expression`);

  // Mobile card style — full width, bottom sheet
  const mobileCardStyle = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    top: 'auto',
    width: '100%',
    zIndex: 10000,
    fontFamily: "'DM Sans', sans-serif",
    animation: 'ob-pop .36s cubic-bezier(.22,1,.36,1) both',
  };

  // Desktop card style — positioned near element
  const desktopCardStyle = {
    position: 'fixed',
    width: '360px',
    zIndex: 10000,
    fontFamily: "'DM Sans', sans-serif",
    transition: 'top .42s cubic-bezier(.22,1,.36,1), bottom .42s cubic-bezier(.22,1,.36,1), left .42s cubic-bezier(.22,1,.36,1), right .42s cubic-bezier(.22,1,.36,1), opacity .3s ease',
    animation: 'ob-pop .36s cubic-bezier(.22,1,.36,1) both',
    top: cardPos.top,
    bottom: cardPos.bottom,
    left: cardPos.left,
    right: cardPos.right,
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500&display=swap');

        .ob-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.42);
          z-index: 9990;
          pointer-events: none;
        }

        .onboarding-highlight {
          position: relative !important;
          z-index: 9995 !important;
          border-radius: 10px;
          animation: ob-hl-pulse 2.4s ease-in-out infinite;
          pointer-events: auto;
        }
        @keyframes ob-hl-pulse {
          0%,100% { box-shadow: 0 0 0 3px ${accent}, 0 0 0 9999px rgba(0,0,0,0.42), 0 0 28px 6px ${glow}; }
          50%      { box-shadow: 0 0 0 5px ${accent}, 0 0 0 9999px rgba(0,0,0,0.42), 0 0 40px 12px ${glow}; }
        }

        @keyframes ob-pop {
          from { opacity:0; transform:scale(.94) translateY(10px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes ob-float {
          0%,100% { transform:translateX(-50%) translateY(0); }
          50%      { transform:translateX(-50%) translateY(-6px); }
        }

        .ob-shell {
          background: #0f2318;
          border: 1px solid rgba(100,180,140,.18);
          position: relative; overflow: visible;
          box-shadow: 0 -8px 40px rgba(0,0,0,.6), 0 0 60px -18px ${glow};
        }
        .ob-shell::before {
          content:''; position:absolute; top:-1px; left:28%; right:28%; height:2px;
          background: linear-gradient(90deg, transparent, ${accent}, transparent);
          border-radius:2px;
        }

        /* ── Mobile shell — bottom sheet rounded top ── */
        .ob-shell-mobile {
          border-radius: 20px 20px 0 0;
        }
        /* ── Desktop shell — fully rounded ── */
        .ob-shell-desktop {
          border-radius: 20px;
        }

        .ob-hero-mobile {
          height: 130px;
          border-radius: 18px 18px 0 0;
          overflow: hidden; position: relative;
          background: radial-gradient(ellipse at 50% 115%, ${glow} 0%, transparent 68%), #1a3a2a;
          display: flex; align-items: flex-end; justify-content: center;
        }
        .ob-hero-desktop {
          height: 185px;
          border-radius: 18px 18px 0 0;
          overflow: hidden; position: relative;
          background: radial-gradient(ellipse at 50% 115%, ${glow} 0%, transparent 68%), #1a3a2a;
        }
        .ob-hero-mobile::after, .ob-hero-desktop::after {
          content:''; position:absolute; inset:0;
          background-image: radial-gradient(rgba(255,255,255,.045) 1px, transparent 1px);
          background-size:18px 18px; pointer-events:none;
        }

        /* Mobile: char smaller, no float, side by side layout */
        .ob-char-mobile {
          position: absolute; bottom: -6px; left: 50%;
          transform: translateX(-50%);
          height: 138px; width: auto; object-fit: contain;
          filter: drop-shadow(0 8px 20px rgba(0,0,0,.55));
          animation: ob-float 3.6s ease-in-out infinite;
          z-index: 1;
        }
        .ob-char-desktop {
          position: absolute; bottom: -8px; left: 50%;
          transform: translateX(-50%); height: 190px; width: auto;
          object-fit: contain;
          filter: drop-shadow(0 10px 28px rgba(0,0,0,.55));
          animation: ob-float 3.6s ease-in-out infinite;
          z-index: 1;
        }
        .ob-char-mobile.fade, .ob-char-desktop.fade { opacity: 0; transition: opacity .15s; }

        .ob-pill {
          position:absolute; font-size:10px; font-weight:500;
          color:rgba(200,232,212,.85);
          background:rgba(15,35,24,.85); border:1px solid rgba(100,180,140,.18);
          backdrop-filter:blur(10px); padding:3px 9px; border-radius:20px;
          z-index:2; white-space:nowrap;
        }
        .ob-pill-n { top:10px; left:11px; font-family:'Syne',sans-serif; font-weight:700; font-size:10.5px; letter-spacing:.06em; color:${accent}; }
        .ob-pill-e { top:10px; right:11px; }

        /* Mobile body — horizontal layout: char left, text right */
        .ob-body-mobile {
          padding: 12px 16px 16px;
          display: flex; flex-direction: column; gap: 0;
        }
        .ob-body-desktop { padding: 16px 20px 18px; }

        .ob-label { font-size:10px; font-weight:500; letter-spacing:.12em; text-transform:uppercase; color:${accent}; margin-bottom:3px; }
        .ob-title-mobile  { font-family:'Syne',sans-serif; font-size:16px; font-weight:800; color:#c8e8d4; margin:0 0 6px; line-height:1.15; letter-spacing:-.02em; }
        .ob-title-desktop { font-family:'Syne',sans-serif; font-size:20px; font-weight:800; color:#c8e8d4; margin:0 0 8px; line-height:1.1; letter-spacing:-.025em; }
        .ob-text-mobile   { font-size:12px; line-height:1.55; color:rgba(160,210,185,.8); margin-bottom:12px; }
        .ob-text-desktop  { font-size:13.5px; line-height:1.65; color:rgba(160,210,185,.8); margin-bottom:16px; }

        .ob-segs { display:flex; gap:3px; margin-bottom:12px; }
        .ob-seg  { height:3px; flex:1; border-radius:3px; background:rgba(100,180,140,.15); cursor:pointer; transition:all .3s; }
        .ob-seg:hover { background:rgba(100,180,140,.3); }
        .ob-seg.on   { flex:2.5; background:${accent}; }
        .ob-seg.past { background:rgba(100,180,140,.3); }

        .ob-foot { display:flex; justify-content:space-between; align-items:center; }
        .ob-skip { font-size:11px; background:none; border:none; color:rgba(160,210,185,.45); cursor:pointer; display:flex; align-items:center; gap:4px; transition:color .18s; font-family:'DM Sans',sans-serif; }
        .ob-skip:hover { color:rgba(160,210,185,.8); }
        .ob-nav { display:flex; gap:6px; }
        .ob-btn-mobile  { font-family:'Syne',sans-serif; font-size:11.5px; font-weight:700; padding:8px 14px; border-radius:9px; border:none; cursor:pointer; display:flex; align-items:center; gap:4px; letter-spacing:.02em; transition:all .18s; }
        .ob-btn-desktop { font-family:'Syne',sans-serif; font-size:12.5px; font-weight:700; padding:9px 17px; border-radius:10px; border:none; cursor:pointer; display:flex; align-items:center; gap:5px; letter-spacing:.025em; transition:all .18s; }
        .ob-back { background:rgba(100,180,140,.08); color:rgba(160,210,185,.6); border:1px solid rgba(100,180,140,.18); }
        .ob-back:hover { background:rgba(100,180,140,.16); color:rgba(200,232,212,.9); }
        .ob-next { background:${accent}; color:#061008; box-shadow:0 4px 16px ${glow}; }
        .ob-next:hover { filter:brightness(1.12); transform:translateY(-2px); box-shadow:0 8px 22px ${glow}; }
        .ob-next:active { transform:scale(.97); }

        /* Mobile drag handle */
        .ob-handle {
          width: 36px; height: 4px;
          background: rgba(100,180,140,.25); border-radius: 2px;
          margin: 10px auto 0; display: block;
        }

        /* Explicit close control — always present, independent of the Skip
           button in the footer, so dismissing the tour is never something
           the member has to hunt for. */
        .ob-close {
          position: absolute; top: 10px; right: 10px; z-index: 3;
          width: 26px; height: 26px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: rgba(6,16,10,.55); border: 1px solid rgba(100,180,140,.28);
          color: rgba(200,232,212,.85); cursor: pointer;
          transition: background .18s, color .18s;
        }
        .ob-close:hover { background: rgba(6,16,10,.8); color: #fff; }
      `}</style>

      <div className="ob-overlay" />

      {isMobile ? (
        /* ══ MOBILE — bottom sheet ══ */
        <div style={mobileCardStyle}>
          <div className="ob-shell ob-shell-mobile">
            <button className="ob-close" onClick={handleSkip} aria-label={t('onboarding.skipTour')}>
              <X size={14} />
            </button>
            <div className="ob-handle" />

            {/* Hero — compact */}
            <div className="ob-hero-mobile">
              <div className="ob-pill ob-pill-n">{currentStep + 1}/{STEPS.length}</div>
              <div className="ob-pill ob-pill-e">{stepExpression}</div>
              <img
                className={'ob-char-mobile' + (animating ? ' fade' : '')}
                src={s.character} alt={stepTitle}
                onError={e => { e.target.onerror = null; e.target.src = `https://via.placeholder.com/100x138/111820/ffffff?text=${s.id.slice(0,2).toUpperCase()}`; }}
              />
            </div>

            {/* Body */}
            <div className="ob-body-mobile">
              <div className="ob-label">{stepSubtitle}</div>
              <h2 className="ob-title-mobile">{stepTitle}</h2>
              <p className="ob-text-mobile">{stepContent}</p>

              {/* Progress dots */}
              <div className="ob-segs">
                {STEPS.map((_, i) => (
                  <div key={i} className={'ob-seg' + (i === currentStep ? ' on' : i < currentStep ? ' past' : '')} onClick={() => goToStep(i)} />
                ))}
              </div>

              {/* Footer */}
              <div className="ob-foot">
                <button className="ob-skip" onClick={handleSkip}>
                  <SkipForward size={11} /> {t('onboarding.skip')}
                </button>
                <div className="ob-nav">
                  {currentStep > 0 && (
                    <button className="ob-btn-mobile ob-back" onClick={handlePrev}>
                      <ChevronLeft size={13} /> {t('onboarding.back')}
                    </button>
                  )}
                  <button className="ob-btn-mobile ob-next" onClick={handleNext}>
                    {currentStep === STEPS.length - 1
                      ? <><Check size={13} /> {t('onboarding.start')}</>
                      : <>{t('onboarding.next')} <ChevronRight size={13} /></>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ══ DESKTOP — floating card ══ */
        <div style={desktopCardStyle}>
          <div className="ob-shell ob-shell-desktop">
            <button className="ob-close" onClick={handleSkip} aria-label={t('onboarding.skipTour')}>
              <X size={14} />
            </button>
            <Arrow side={cardPos.arrowSide} accent={accent} />

            <div className="ob-hero-desktop">
              <div className="ob-pill ob-pill-n">{currentStep + 1} / {STEPS.length}</div>
              <div className="ob-pill ob-pill-e">{stepExpression}</div>
              <img
                className={'ob-char-desktop' + (animating ? ' fade' : '')}
                src={s.character} alt={stepTitle}
                onError={e => { e.target.onerror = null; e.target.src = `https://via.placeholder.com/140x190/111820/ffffff?text=${s.id.slice(0,2).toUpperCase()}`; }}
              />
            </div>

            <div className="ob-body-desktop">
              <div className="ob-label">{stepSubtitle}</div>
              <h2 className="ob-title-desktop">{stepTitle}</h2>
              <p className="ob-text-desktop">{stepContent}</p>

              <div className="ob-segs">
                {STEPS.map((_, i) => (
                  <div key={i} className={'ob-seg' + (i === currentStep ? ' on' : i < currentStep ? ' past' : '')} onClick={() => goToStep(i)} />
                ))}
              </div>

              <div className="ob-foot">
                <button className="ob-skip" onClick={handleSkip}>
                  <SkipForward size={12} /> {t('onboarding.skipTour')}
                </button>
                <div className="ob-nav">
                  {currentStep > 0 && (
                    <button className="ob-btn-desktop ob-back" onClick={handlePrev}>
                      <ChevronLeft size={14} /> {t('onboarding.back')}
                    </button>
                  )}
                  <button className="ob-btn-desktop ob-next" onClick={handleNext}>
                    {currentStep === STEPS.length - 1
                      ? <><Check size={14} /> {t('onboarding.getStarted')}</>
                      : <>{t('onboarding.next')} <ChevronRight size={14} /></>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingGuide;